/**
 * Grid Terrain Bridge
 * Translates grid-map Cell terrain/walls into combat effects (cover, movement cost, obstacles).
 */

import type { GridMap, Cell } from '@omen-quest/grid-map';
import { inBounds } from '@omen-quest/grid-map';

// ============================================================================
// COMBAT TERRAIN EFFECTS
// ============================================================================

export interface CellCombatEffects {
  /** Cover level: 0 = none, 1 = light (-1 attacker die), 2 = heavy (-2 attacker dice) */
  coverLevel: number;
  /** Extra AP cost to enter this cell (difficult terrain) */
  moveCostExtra: number;
  /** Whether the cell is impassable (wall/void — already handled by pathfinding) */
  isObstacle: boolean;
}

/**
 * Compute combat effects for a cell based on its terrain and wall configuration.
 *
 * Cover is derived from wall edges — a cell with walls on 2+ sides offers light cover,
 * 3+ sides offers heavy cover. This naturally creates cover from dungeon architecture
 * (e.g., doorways, alcoves, corners) without needing separate cover entities.
 *
 * Water terrain adds +1 AP to movement (difficult terrain).
 */
export function getCellCombatEffects(map: GridMap, x: number, y: number): CellCombatEffects {
  if (!inBounds(map, x, y)) {
    return { coverLevel: 0, moveCostExtra: 0, isObstacle: true };
  }

  const cell = map.cells[y][x];

  // Obstacles: wall/void terrain
  if (cell.terrain === 'wall' || cell.terrain === 'void') {
    return { coverLevel: 0, moveCostExtra: 0, isObstacle: true };
  }

  // Count wall edges for cover
  const wallCount = countWalls(cell);
  let coverLevel = 0;
  if (wallCount >= 3) coverLevel = 2;      // Heavy cover: 3+ walls (alcove/dead end)
  else if (wallCount >= 2) coverLevel = 1;  // Light cover: 2 walls (corner/doorway)

  // Difficult terrain: water costs extra AP
  let moveCostExtra = 0;
  if (cell.terrain === 'water') moveCostExtra = 1;

  return { coverLevel, moveCostExtra, isObstacle: false };
}

/**
 * Count the number of wall edges on a cell.
 */
function countWalls(cell: Cell): number {
  let count = 0;
  if (cell.walls.N) count++;
  if (cell.walls.E) count++;
  if (cell.walls.S) count++;
  if (cell.walls.W) count++;
  return count;
}

/**
 * Get cover penalty for an attacker shooting at a defender on a given tile.
 * Returns the number of dice to subtract from the attacker's pool.
 */
export function getGridCoverPenalty(map: GridMap, defenderX: number, defenderY: number): number {
  return getCellCombatEffects(map, defenderX, defenderY).coverLevel;
}

/**
 * Get extra movement cost for entering a tile.
 */
export function getGridMoveCostExtra(map: GridMap, x: number, y: number): number {
  return getCellCombatEffects(map, x, y).moveCostExtra;
}

/**
 * Get total movement cost for entering a tile (base 1 + terrain extra).
 * Normal terrain = 1, water = 2. Used as tileCostFn for pathfinding.
 */
export function getGridTileMoveCost(map: GridMap, x: number, y: number): number {
  return 1 + getGridMoveCostExtra(map, x, y);
}
